import { CloseIcon, InfoIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Center,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Select,
  Spinner,
  useDisclosure,
} from "@chakra-ui/react";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { SelectTableAction } from "@nens/chakra/components";
import { RanaProjectSelect, TableFilterCheckbox, TablePagination } from "@nens/ranalib";
import { ThreediModel } from "@nens/threedi/openapi-client";

import ModelsSvg from "@/assets/models.svg?url";

import { APP_BASE_HREF } from "../../App";
import { ExplainSideColumn } from "../../components/ExplainSideColumn";
import { useUser } from "../../providers/AuthProvider";
import { OrganisationContext } from "../../providers/OrganisationProvider";
import { useNeedsCreatorRoleTitle } from "../../queries/general";
import { ID } from "../../queries/queryClient";
import { useDeleteMultipleModels, useThreediModelsByOwner } from "../../queries/threedimodels";
import RemoveMultipleModal from "../RemoveMultipleModal";

import { ModelListHelpText } from "./ModelListHelpText";
import ModelsExcelButton from "./ModelsExcelButton";
import ThreediModelBlock from "./ThreediModelBlock";

const searchFieldDefault = "name";
let lastSearchField = searchFieldDefault;

const buildModelsQueryParams = (
  page: number,
  itemsPerPage: number,
  searchField: string,
  searchString: string,
  showOnlyMine: boolean,
  username: string,
  selectedProjectId: string | null
) => {
  const params: {
    offset: number;
    limit: number;
    nameIcontains?: string;
    id?: number;
    revisionCommitUserUsernameIstartswith?: string;
    revisionSchematisationTagsIn?: string;
    revisionSchematisationNameIcontains?: string;
    revisionSchematisationRanaProjectId?: string;
    revisionSchematisationRanaProjectIdIsnull?: boolean;
  } = {
    offset: (page - 1) * itemsPerPage,
    limit: itemsPerPage,
    // If there is no selectedProjectId then we want to filter on projectId=null, which has
    // a slightly different syntax. ranaProjectId: null would filter on the *string* "null".
    ...(selectedProjectId
      ? { revisionSchematisationRanaProjectId: selectedProjectId }
      : { revisionSchematisationRanaProjectIdIsnull: true }),
  };

  // Handle "show only mine" filter
  if (showOnlyMine) {
    params.revisionCommitUserUsernameIstartswith = username;
    return params;
  }

  // Handle search filters based on field
  if (!searchString) {
    return params;
  }

  switch (searchField) {
    case "name":
      params.nameIcontains = searchString;
      break;
    case "id":
      params.id = Number(searchString);
      break;
    case "username":
      params.revisionCommitUserUsernameIstartswith = searchString;
      break;
    case "tag":
      params.revisionSchematisationTagsIn = searchString;
      break;
    case "project":
      params.revisionSchematisationTagsIn = `project: ${searchString}`;
      break;
    case "schematisationName":
      params.revisionSchematisationNameIcontains = searchString;
      break;
  }

  return params;
};

const ModelsList: FC = () => {
  const { organisation } = useContext(OrganisationContext);
  const navigate = useNavigate();
  const user = useUser();

  const clickModelsMapButton = useCallback(
    () => navigate(`${APP_BASE_HREF}/modelsmap`),
    [navigate]
  );
  const { isOpen, onOpen, onClose } = useDisclosure();
  const needsCreatorTitle = useNeedsCreatorRoleTitle();
  const [selectedRows, setSelectedRows] = useState<ThreediModel[]>([]);
  const [deleteIds, setDeleteIds] = useState<ID[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState<number>(20);
  const [page, setPage] = useState<number>(1);

  const [searchString, setSearchString] = useState<string>("");
  const [searchField, setSearchField] = useState<string>(searchFieldDefault);
  const [showOnlyMine, setShowOnlyMine] = useState<boolean>(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  // Makes sure either user search string or only show my own exports is true or filled
  useEffect(() => {
    if (searchField === "username" && searchField === lastSearchField && showOnlyMine)
      setSearchString("");
    lastSearchField = searchField;
  }, [searchField, showOnlyMine]);

  useEffect(() => {
    if (searchField === "username" && searchString) return setShowOnlyMine(false);
  }, [searchField, searchString]);

  const modelsResponse = useThreediModelsByOwner(
    organisation.uniqueId,
    buildModelsQueryParams(
      page,
      itemsPerPage,
      searchField,
      searchString,
      showOnlyMine,
      user.username,
      selectedProjectId
    )
  );

  const numPages = modelsResponse.data
    ? Math.ceil(modelsResponse.data.count / itemsPerPage)
    : undefined;

  useEffect(() => setPage(1), [itemsPerPage]);
  useEffect(() => {
    document.documentElement.scrollTop = 0;
  }, [page]);

  const searchFields = [
    ["name", "Model name"],
    ["username", "Created by"],
    ["tag", "Tag"],
    ["project", "Project"],
    ["id", "ID (Exact)"],
    ["schematisationName", "Schematisation name"],
  ];

  const renderModels = () => {
    if (!modelsResponse.isSuccess) {
      return (
        <Center width="full" height="6xl">
          <Spinner size="xl" />
        </Center>
      );
    }

    if (!modelsResponse.data.results.length) {
      return (
        <Center width="full" height="500px">
          There is no data to be shown with these filters
        </Center>
      );
    }

    return modelsResponse.data.results.map((threediModel, idx) => (
      <ThreediModelBlock
        key={idx}
        threediModel={threediModel}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />
    ));
  };

  return (
    <ExplainSideColumn
      imgUrl={ModelsSvg}
      imgAltDescription="Models icon"
      headerText="Models"
      explanationText={<ModelListHelpText />}
      backUrl={"/"}
    >
      <Box sx={{ maxWidth: "1500px" }}>
        <Box maxW="400px" mb={4}>
          <RanaProjectSelect
            value={selectedProjectId}
            onChange={(projectId) => {
              setSelectedProjectId(projectId);
              setPage(1);
            }}
          />
        </Box>
        <Flex justifyContent="space-between" sx={{ marginTop: "8px", marginBottom: "16px" }}>
          <TableFilterCheckbox
            id="my-models-checkbox"
            value={showOnlyMine}
            onChange={setShowOnlyMine}
            sx={{ m: 0 }}
          >
            Show my models
          </TableFilterCheckbox>
          <Flex>
            <Button variant="secondary" marginRight="4" onClick={clickModelsMapButton}>
              Switch to map view
            </Button>
            <ModelsExcelButton
              disabled={
                !modelsResponse.isSuccess ||
                !modelsResponse.data ||
                !modelsResponse.data.results.length
              }
            />
          </Flex>
        </Flex>
        <Flex gap={4}>
          <Flex>
            <InputGroup>
              <InputLeftElement color="gray.300" children={<SearchIcon />} />
              <Input
                value={searchString}
                onChange={(event) => setSearchString(event.target.value)}
                placeholder="Type to search"
                id="search-string-input"
              />
              <InputRightElement color="gray.300">
                <CloseIcon onClick={() => setSearchString("")} />
              </InputRightElement>
            </InputGroup>
          </Flex>
          <Flex>
            <Select
              value={searchField}
              onChange={(event) => setSearchField(event.target.value)}
              id="search-field-select"
              height="48px" // same as search-string-input
            >
              {searchFields.map(([value, description], idx) => (
                <option key={`${value}-${idx}`} value={value}>
                  Search: {description}
                </option>
              ))}
            </Select>
          </Flex>
        </Flex>
        <SelectTableAction
          sx={{
            backgroundColor: "brand.glacier",
            borderWidth: "base",
            borderStyle: "solid",
            borderColor: "brand.border",
            borderRadius: "4px",
            mt: "16px",
            fontWeight: "normal",
            "& button": {
              color: "brand.deep_sea",
            },
          }}
          deselectAll={() => setSelectedRows([])}
          tableActions={[
            {
              label: "Delete selected",
              isDisabledTitle: needsCreatorTitle,
              action: (rows) => {
                setDeleteIds(rows.map((row) => row.id));
                onOpen();
              },
            },
          ]}
          rows={selectedRows}
          icon={<InfoIcon boxSize={5} color="brand.clear_creek" />}
        />
        <RemoveMultipleModal
          resetSelectedRows={setSelectedRows}
          isOpen={isOpen}
          onClose={onClose}
          deleteMultipleFunction={useDeleteMultipleModels(deleteIds)}
        >
          This deletes all the selected models. This action cannot be undone.
        </RemoveMultipleModal>
        {renderModels()}
        <TablePagination
          limit={itemsPerPage}
          numPages={numPages ?? 0}
          page={page}
          setPageSize={setItemsPerPage}
          setPage={setPage}
        />
      </Box>
    </ExplainSideColumn>
  );
};

export default ModelsList;
